import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, Trash2 } from 'lucide-react';
import { useUser } from '../../contexts/UserContext';
import {
  GRATITUDE_LINES, GRATITUDE_LINE_MAX, GRATITUDE_NOTE_MAX, loadEntries, saveEntries, makeEntry, addEntry, promptFor, groupByDay,
} from '../../lib/gratitudeJournal';

// Three Good Things: a gratitude journal saved on this device only.
// ⚠️ The text never leaves the device — see src/lib/gratitudeJournal.js. Plain
// textareas, NOT PostEditor: its @mention suggestions send fragments to the server.

const headerStyle = {
  backgroundColor: 'var(--header-bg)',
  borderBottom: '1px solid rgba(255,255,255,0.1)',
  paddingTop: 'var(--sat)',
};

const cardStyle = {
  backgroundColor: 'var(--card-bg)',
  border: '1px solid var(--card-border)',
};

const EMPTY_LINES = Array(GRATITUDE_LINES).fill('');

// One line tall to start, growing with the text, so the saved entries stay on
// the first screen of a phone.
const autosize = (el) => {
  if (!el) return;
  el.style.height = 'auto';
  el.style.height = `${el.scrollHeight}px`;
};

const formatTime = (iso) =>
  new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });

const GratitudeScreen = () => {
  const navigate = useNavigate();
  const { session } = useUser();
  const userId = session?.user?.id;

  const [entries, setEntries] = useState(() => loadEntries(userId));
  const [lines, setLines] = useState(EMPTY_LINES);
  const [note, setNote] = useState('');
  const [justSaved, setJustSaved] = useState(false);
  const [storageFailed, setStorageFailed] = useState(false);
  const [confirmId, setConfirmId] = useState(null);
  const [confirmAll, setConfirmAll] = useState(false);
  const lineRefs = useRef([]);
  const noteRef = useRef(null);

  const backToRelax = () => navigate('/explore/relax');

  const commit = (next) => {
    setEntries(next);
    setStorageFailed(!saveEntries(userId, next));
  };

  const setLine = (i) => (e) => {
    setJustSaved(false);
    autosize(e.target);
    setLines((prev) => prev.map((l, j) => (j === i ? e.target.value : l)));
  };

  const changeNote = (e) => {
    setJustSaved(false);
    autosize(e.target);
    setNote(e.target.value);
  };

  const save = () => {
    const entry = makeEntry(lines, note);
    if (!entry) return;
    commit(addEntry(entries, entry));
    setLines(EMPTY_LINES);
    setNote('');
    // Back to their starting height once React has cleared the values.
    requestAnimationFrame(() => [...lineRefs.current, noteRef.current].forEach(autosize));
    setJustSaved(true);
  };

  const remove = (id) => {
    commit(entries.filter((e) => e.id !== id));
    setConfirmId(null);
  };

  const removeAll = () => {
    commit([]);
    setConfirmAll(false);
  };

  const canSave = lines.some((l) => l.trim()) || !!note.trim();

  return (
    <div className="min-h-screen pb-nav">
      <div className="sticky top-0 z-20 md:hidden" style={headerStyle}>
        <div className="max-w-2xl mx-auto px-4 py-2">
          <button
            onClick={backToRelax}
            className="flex items-center gap-1 mb-3 text-sm transition-colors text-white/70 hover:text-white"
          >
            <ChevronLeft size={18} />
            Relax
          </button>
          <h1 className="text-xl font-bold text-white tracking-tight">Three Good Things</h1>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 pt-4 md:pt-5">
        <div className="hidden md:block mb-3">
          <button
            onClick={backToRelax}
            className="flex items-center gap-1 mb-2 text-sm transition-opacity hover:opacity-80"
            style={{ color: 'var(--text-tertiary)' }}
          >
            <ChevronLeft size={16} />
            Relax
          </button>
          <h2 className="text-xl font-bold" style={{ color: 'var(--text-primary)' }}>Three Good Things</h2>
        </div>

        <section className="space-y-3">
          <p className="text-[15px] leading-relaxed" style={{ color: 'var(--text-secondary)' }}>
            What went right today? Big or small, write down up to three.
          </p>
          {/* Above the boxes, not under the button: the promise comes before
              anyone writes, as on the Rant Room. */}
          <p className="text-xs leading-relaxed" style={{ color: 'var(--text-tertiary)' }}>
            Saved on this device only. Nobody else can see it, including us. Deleting the app or clearing your browser data deletes your entries.
          </p>
          <p className="text-sm leading-snug" style={{ color: 'var(--text-tertiary)' }}>
            <span className="font-semibold" style={{ color: 'var(--text-secondary)' }}>Need an idea? </span>
            {promptFor()}
          </p>

          <div className="rounded-2xl p-3 space-y-2" style={cardStyle}>
            {lines.map((value, i) => (
              <div key={i} className="flex items-start gap-2.5">
                <span
                  aria-hidden="true"
                  className="mt-2 w-6 h-6 flex-shrink-0 rounded-full flex items-center justify-center text-xs font-bold text-white"
                  style={{ backgroundColor: 'var(--brand-primary)' }}
                >
                  {i + 1}
                </span>
                <textarea
                  id={`gratitude-line-${i}`}
                  ref={(el) => { lineRefs.current[i] = el; }}
                  value={value}
                  onChange={setLine(i)}
                  aria-label={`Good thing ${i + 1}`}
                  maxLength={GRATITUDE_LINE_MAX}
                  rows={1}
                  autoComplete="off"
                  className="flex-1 min-w-0 px-3 py-2 rounded-lg text-[15px] leading-snug outline-none resize-none placeholder:text-[color:var(--text-tertiary)]"
                  style={{ backgroundColor: 'var(--input-bg)', border: '1px solid var(--input-border)', color: 'var(--text-primary)' }}
                  placeholder={i === 0 ? 'One good thing…' : 'Another (optional)'}
                />
              </div>
            ))}

            {/* Not numbered: it isn't a fourth good thing, just room for the rest of the day. */}
            <div className="pt-2 mt-1 border-t" style={{ borderColor: 'var(--card-border)' }}>
              <label htmlFor="gratitude-note" className="block text-sm font-semibold mb-1.5" style={{ color: 'var(--text-secondary)' }}>
                Anything else about your day? <span className="font-normal" style={{ color: 'var(--text-tertiary)' }}>(optional)</span>
              </label>
              <textarea
                id="gratitude-note"
                ref={noteRef}
                value={note}
                onChange={changeNote}
                maxLength={GRATITUDE_NOTE_MAX}
                rows={2}
                autoComplete="off"
                className="w-full px-3 py-2 rounded-lg text-[15px] leading-snug outline-none resize-none placeholder:text-[color:var(--text-tertiary)]"
                style={{ backgroundColor: 'var(--input-bg)', border: '1px solid var(--input-border)', color: 'var(--text-primary)' }}
                placeholder="How it went, how you're feeling…"
              />
            </div>
          </div>

          <button
            onClick={save}
            disabled={!canSave}
            className="w-full rounded-2xl py-3 px-4 text-[15px] font-bold text-white transition-all hover:brightness-110 active:scale-[0.98] disabled:opacity-45 disabled:active:scale-100 disabled:hover:brightness-100"
            style={{ backgroundColor: 'var(--brand-primary)' }}
          >
            Save to my journal
          </button>
          {/* Always mounted so screen readers hear it; takes no space until a save. */}
          <p
            role="status"
            className={justSaved ? 'text-sm text-center font-semibold' : 'sr-only'}
            style={{ color: 'var(--brand-primary)' }}
          >
            {justSaved ? 'Saved. Nice work noticing it.' : ''}
          </p>
          {storageFailed && (
            <p className="text-xs text-center rounded-lg px-3 py-2" style={{ backgroundColor: 'rgba(192,57,43,0.1)', color: '#C0392B' }}>
              This browser isn't letting DSP Cafe save. Your entries will disappear when you leave this page.
            </p>
          )}
        </section>

        <section className="mt-8 space-y-4 pb-4">
          <h2 className="text-base font-bold" style={{ color: 'var(--text-primary)' }}>Your journal</h2>

          {entries.length === 0 && (
            <p className="text-sm" style={{ color: 'var(--text-tertiary)' }}>
              Your good things will collect here, so you can look back on them on a hard day.
            </p>
          )}

          {groupByDay(entries).map((group) => (
            <div key={group.label} className="space-y-2">
              <h3 className="text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--text-tertiary)' }}>
                {group.label}
              </h3>
              {group.entries.map((entry) => (
                <article key={entry.id} className="rounded-xl p-3.5 flex items-start gap-3" style={cardStyle}>
                  <div className="flex-1 min-w-0 space-y-2">
                    {entry.lines.length > 0 && (
                      <ul className="space-y-1.5">
                        {entry.lines.map((line, i) => (
                          <li key={i} className="relative pl-3.5 text-[15px] leading-snug break-words" style={{ color: 'var(--text-primary)' }}>
                            <span
                              aria-hidden="true"
                              className="absolute left-0.5 top-[0.55em] w-[5px] h-[5px] rounded-full opacity-60"
                              style={{ backgroundColor: 'var(--brand-primary)' }}
                            />
                            {line}
                          </li>
                        ))}
                      </ul>
                    )}
                    {entry.note && (
                      <p
                        className={`text-sm leading-relaxed whitespace-pre-wrap break-words ${entry.lines.length > 0 ? 'pt-2 border-t' : ''}`}
                        style={{ color: 'var(--text-secondary)', borderColor: 'var(--card-border)' }}
                      >
                        {entry.note}
                      </p>
                    )}
                  </div>
                  <div className="flex flex-col items-end gap-1 flex-shrink-0">
                    <span className="text-xs" style={{ color: 'var(--text-tertiary)' }}>{formatTime(entry.createdAt)}</span>
                    {confirmId === entry.id ? (
                      <button
                        onClick={() => remove(entry.id)}
                        className="px-2 py-1 rounded-lg text-xs font-semibold text-white transition-all active:scale-95"
                        style={{ backgroundColor: '#C0392B' }}
                      >
                        Delete?
                      </button>
                    ) : (
                      <button
                        onClick={() => setConfirmId(entry.id)}
                        aria-label="Delete this entry"
                        className="p-1.5 rounded-lg transition-all hover:brightness-95 active:scale-95"
                        style={{ color: 'var(--text-tertiary)' }}
                      >
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>
                </article>
              ))}
            </div>
          ))}

          {entries.length > 0 && (
            <div className="pt-2 flex flex-wrap items-center gap-2">
              {confirmAll ? (
                <>
                  <span className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                    Delete all {entries.length} {entries.length === 1 ? 'entry' : 'entries'}? This can't be undone.
                  </span>
                  <button
                    onClick={removeAll}
                    className="px-3 py-1.5 rounded-lg text-xs font-semibold text-white transition-all active:scale-95"
                    style={{ backgroundColor: '#C0392B' }}
                  >
                    Delete all
                  </button>
                  <button
                    onClick={() => setConfirmAll(false)}
                    className="px-3 py-1.5 rounded-lg text-xs font-semibold transition-all active:scale-95"
                    style={{ backgroundColor: 'var(--pill-bg)', color: 'var(--text-secondary)' }}
                  >
                    Cancel
                  </button>
                </>
              ) : (
                <button
                  onClick={() => setConfirmAll(true)}
                  className="text-xs font-semibold underline-offset-2 hover:underline"
                  style={{ color: 'var(--text-tertiary)' }}
                >
                  Delete all entries
                </button>
              )}
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

export default GratitudeScreen;
